import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { customersAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Separator } from '../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Users, Phone, Mail, MapPin, X, User, ShoppingBag, Wallet, MessageSquare, ExternalLink, Copy, Check, Eye, BookUser } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import PaywallDialog from '../../components/PaywallDialog';

// Конфигурация соцсетей с подсказками
export const SOCIAL_NETWORKS = {
  telegram: {
    name: 'Telegram',
    color: '#0088cc',
    placeholder: '@username или ссылка',
    hint: 'Например: @ivan_petrov или https://t.me/ivan_petrov',
    getLink: (v) => v.startsWith('http') ? v : `https://t.me/${v.replace('@', '')}`
  },
  whatsapp: {
    name: 'WhatsApp',
    color: '#25D366',
    placeholder: '+7XXXXXXXXXX',
    hint: 'Номер телефона с кодом страны',
    getLink: (v) => {
      let digits = v.replace(/\D/g, '');
      if (digits.startsWith('8') && digits.length === 11) digits = '7' + digits.slice(1);
      return `https://api.whatsapp.com/send?phone=${digits}`;
    }
  },
  instagram: {
    name: 'Instagram',
    color: '#E4405F',
    placeholder: '@username или ссылка',
    hint: 'Например: @shop_name или https://instagram.com/shop_name',
    getLink: (v) => v.startsWith('http') ? v : `https://instagram.com/${v.replace('@', '')}`
  },
  vk: {
    name: 'ВКонтакте',
    color: '#0077FF',
    placeholder: 'id или username',
    hint: 'Например: id123456 или username',
    getLink: (v) => v.startsWith('http') ? v : `https://vk.com/${v}`
  },
  ok: {
    name: 'Одноклассники',
    color: '#EE8208',
    placeholder: 'ID профиля или ссылка',
    hint: 'Например: 123456789 или https://ok.ru/profile/123456789',
    getLink: (v) => v.startsWith('http') ? v : `https://ok.ru/profile/${v}`
  },
  vkmax: {
    name: 'Max',
    color: '#07F',
    placeholder: 'Ссылка или номер телефона',
    hint: 'Например: +7 900 123-45-67 или https://max.ru/u/...',
    getLink: (v) => {
      if (v.startsWith('http')) return v;
      if (/^[\d\s\-+()]+$/.test(v.trim())) return null;
      return `https://max.ru/u/${v}`;
    }
  },
  facebook: {
    name: 'Facebook',
    color: '#1877F2',
    placeholder: 'username или ссылка',
    hint: 'Например: ivan.petrov или https://facebook.com/ivan.petrov',
    getLink: (v) => v.startsWith('http') ? v : `https://facebook.com/${v}`
  }
};

// Social Icons
export const SocialIcon = ({ type, className }) => {
  const icons = {
    telegram: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>
    ),
    whatsapp: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    ),
    instagram: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
      </svg>
    ),
    vk: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.391 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.12-5.339-3.202-2.17-3.042-2.763-5.32-2.763-5.785 0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.678.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.27-1.422 2.18-3.61 2.18-3.61.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
      </svg>
    ),
    ok: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M14.505 17.44a11.599 11.599 0 003.6-1.49.91.91 0 00.33-1.24.91.91 0 00-1.24-.33 9.76 9.76 0 01-10.39 0 .909.909 0 10-.91 1.57 11.6 11.6 0 003.6 1.49l-3.27 3.27a.91.91 0 101.29 1.29L12 17.45l4.48 4.48a.91.91 0 001.29-1.29l-3.27-3.27v.02zM12 12.18a5.73 5.73 0 110-11.46 5.73 5.73 0 010 11.46zm0-9.64a3.91 3.91 0 100 7.82 3.91 3.91 0 000-7.82z"/>
      </svg>
    ),
    vkmax: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.2 2.88c-5.06 0-9.32 3.91-9.32 9.11 0 2.18.4 3.68.76 5.03.3 1.1.56 2.1.56 3.29.13 1.58 3.04.66 3.96-.53 1.46 1.05 2.29 1.31 4.09 1.31 4.98-.03 9-.41 8.98-9.06C21.23 6.98 17.27 2.88 12.2 2.88zm.07 13.73a4.56 4.56 0 01-2.23-.79c-.4.4-1.04.92-1.29.85-.53-.14-1.15-2.84-.8-5.05.43-2.67 2.35-4.38 4.81-4.25 2.55.15 4.52 2.31 4.42 4.87-.17 2.55-2.35 4.49-4.9 4.37z"/>
      </svg>
    ),
    facebook: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    )
  };
  return icons[type] || null;
};

// Social Link Button Component
export function SocialLinkButton({ type, value, size = 'default' }) {
  if (!value || !SOCIAL_NETWORKS[type]) return null;

  const network = SOCIAL_NETWORKS[type];
  const link = network.getLink(value);
  const sizeClasses = size === 'sm' ? 'h-7 w-7' : 'h-9 w-9';
  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  // If getLink returns null (e.g. phone number for Max), show as non-clickable icon with tooltip
  if (!link) {
    return (
      <span
        className={`inline-flex items-center justify-center rounded-full border border-border ${sizeClasses}`}
        style={{ color: network.color }}
        title={`${network.name}: ${value}`}
      >
        <SocialIcon type={type} className={iconSize} />
      </span>
    );
  }

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center rounded-full border border-border transition-colors ${sizeClasses}`}
      style={{ color: network.color }}
      title={network.name}
      onClick={(e) => e.stopPropagation()}
    >
      <SocialIcon type={type} className={iconSize} />
    </a>
  );
}

// Customer Card Component (for viewing) - Improved Design
export function CustomerCard({ customer, onEdit, onClose }) {
  const [copiedField, setCopiedField] = React.useState(null);
  
  if (!customer) return null;

  const socialLinks = customer.social_links || {};
  const hasSocials = Object.values(socialLinks).some(v => v);

  // Copy to clipboard function
  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success('Скопировано в буфер обмена');
  };

  // Get initials from name
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md p-0 overflow-y-auto [&>button]:hidden" data-testid="customer-card">
        {/* Header with gradient background */}
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pt-8 pb-6">
          {/* Close button - single button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-background/80 transition-colors z-10"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
          
          {/* Avatar and name */}
          <div className="flex items-start gap-4 mt-2">
            <div className="h-18 w-18 rounded-full bg-primary/20 flex items-center justify-center text-primary text-2xl font-bold shadow-sm border-2 border-background" style={{height: '72px', width: '72px'}}>
              {getInitials(customer.name)}
            </div>
            <div className="flex-1 min-w-0 pt-2">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold truncate">{customer.name}</h2>
                <button 
                  onClick={() => copyToClipboard(customer.name, 'name')}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
                  title="Копировать имя"
                >
                  {copiedField === 'name' ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Клиент с {formatDate(customer.created_at) || 'недавнего времени'}
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 pt-6 pb-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20 p-4">
              <ShoppingBag className="absolute -right-2 -bottom-2 h-12 w-12 text-orange-500/10" />
              <div className="relative">
                <p className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wide">Заказов</p>
                <p className="text-2xl font-bold mt-1">{customer.total_orders || 0}</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 p-4">
              <Wallet className="absolute -right-2 -bottom-2 h-12 w-12 text-emerald-500/10" />
              <div className="relative">
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Сумма</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(customer.total_spent || 0)}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          {(customer.phone || customer.social_links?.telegram || customer.social_links?.whatsapp) && (
            <div className="mt-6">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                <MessageSquare className="h-3.5 w-3.5" />
                Быстрые действия
              </h4>
              <div className="flex flex-wrap gap-2">
                {customer.phone && (
                  <a
                    href={`tel:${customer.phone}`}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors text-sm font-medium"
                  >
                    <Phone className="h-4 w-4 text-blue-500" />
                    Позвонить
                  </a>
                )}
                {(customer.social_links?.whatsapp || customer.phone) && (
                  <a
                    href={SOCIAL_NETWORKS.whatsapp.getLink(customer.social_links?.whatsapp || customer.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors text-sm font-medium"
                  >
                    <SocialIcon type="whatsapp" className="h-4 w-4" style={{ color: '#25D366' }} />
                    WhatsApp
                  </a>
                )}
                {customer.social_links?.telegram && (
                  <a
                    href={SOCIAL_NETWORKS.telegram.getLink(customer.social_links.telegram)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors text-sm font-medium"
                  >
                    <SocialIcon type="telegram" className="h-4 w-4" style={{ color: '#0088cc' }} />
                    Telegram
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Contact Info */}
          <div className="mt-8">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
              <User className="h-3.5 w-3.5" />
              Контактная информация
            </h4>
            <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
              {customer.phone && (
                <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors group">
                  <a href={`tel:${customer.phone}`} className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground mb-0.5">Телефон</p>
                      <p className="font-medium truncate">{customer.phone}</p>
                    </div>
                  </a>
                  <button 
                    onClick={() => copyToClipboard(customer.phone, 'phone')}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    title="Копировать"
                  >
                    {copiedField === 'phone' ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              )}
              {customer.email && (
                <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors group">
                  <a href={`mailto:${customer.email}`} className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-violet-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground mb-0.5">Email</p>
                      <p className="font-medium truncate">{customer.email}</p>
                    </div>
                  </a>
                  <button 
                    onClick={() => copyToClipboard(customer.email, 'email')}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    title="Копировать"
                  >
                    {copiedField === 'email' ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              )}
              {customer.address && (
                <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors group">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-rose-500/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-rose-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground mb-0.5">Адрес</p>
                      <p className="font-medium truncate">{customer.address}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(customer.address, 'address')}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    title="Копировать"
                  >
                    {copiedField === 'address' ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              )}
              {!customer.phone && !customer.email && !customer.address && (
                <div className="p-5 text-center text-sm text-muted-foreground">
                  Контактная информация не указана
                </div>
              )}
            </div>
          </div>

          {/* Social Links */}
          {hasSocials && (
            <div className="mt-8">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                <ExternalLink className="h-3.5 w-3.5" />
                Социальные сети
              </h4>
              <div className="flex flex-wrap gap-2.5">
                {Object.entries(socialLinks).map(([key, value]) => {
                  if (!value) return null;
                  const network = SOCIAL_NETWORKS[key];
                  if (!network) return null;
                  const link = network.getLink(value);
                  
                  if (!link) {
                    return (
                      <span
                        key={key}
                        className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-border bg-card text-sm"
                        title={value}
                      >
                        <SocialIcon type={key} className="h-4 w-4" style={{ color: network.color }} />
                        <span className="font-medium">{network.name}</span>
                      </span>
                    );
                  }

                  return (
                    <a
                      key={key}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors text-sm"
                      style={{ '--social-color': network.color }}
                    >
                      <SocialIcon type={key} className="h-4 w-4" style={{ color: network.color }} />
                      <span className="font-medium">{network.name}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          {customer.notes && (
            <div className="mt-8">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                <MessageSquare className="h-3.5 w-3.5" />
                Заметки
              </h4>
              <div className="p-5 rounded-xl bg-muted/50 border border-border">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{customer.notes}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8">
            <Button 
              onClick={onEdit} 
              className="w-full h-12 font-medium text-base"
              variant="default"
            >
              <Edit className="h-4 w-4 mr-2" />
              Редактировать клиента
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Customer Mini Card (inline display in orders)
export function CustomerMiniCard({ customer }) {
  if (!customer) return null;
  const socialLinks = customer.social_links || {};

  return (
    <div className="p-3 rounded-lg border border-border bg-card space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-medium">{customer.name}</span>
        <div className="flex gap-1">
          {Object.entries(socialLinks).slice(0, 3).map(([key, value]) => (
            value && <SocialLinkButton key={key} type={key} value={value} size="sm" />
          ))}
        </div>
      </div>
      {(customer.phone || customer.email) && (
        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
          {customer.phone && (
            <a href={`tel:${customer.phone}`} className="flex items-center gap-1 hover:text-foreground">
              <Phone className="h-3 w-3" /> {customer.phone}
            </a>
          )}
          {customer.email && (
            <a href={`mailto:${customer.email}`} className="flex items-center gap-1 hover:text-foreground">
              <Mail className="h-3 w-3" /> {customer.email}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// Social Link Input with hint
function SocialLinkInput({ type, value, onChange }) {
  const network = SOCIAL_NETWORKS[type];
  if (!network) return null;

  return (
    <div className="space-y-1">
      <Label className="text-xs flex items-center gap-1.5">
        <SocialIcon type={type} className="h-3.5 w-3.5" style={{ color: network.color }} />
        {network.name}
      </Label>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={network.placeholder}
        className="h-9"
      />
      <p className="text-[10px] text-muted-foreground">{network.hint}</p>
    </div>
  );
}

export default function CustomersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const PAGE_SIZE = 15;
  const loadMoreRef = useRef(null);
  const skipRef = useRef(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    social_links: {},
  });
  const [saving, setSaving] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  useEffect(() => {
    loadCustomers();
  }, []);

  // Handle highlight/view parameter to open customer card
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    const viewId = searchParams.get('view');
    const openId = searchParams.get('open');
    const targetId = highlightId || viewId || openId;
    
    if (targetId && customers.length > 0) {
      const customer = customers.find(c => c.id === targetId);
      if (customer) {
        setViewingCustomer(customer);
        // Clear the param from URL
        searchParams.delete('highlight');
        searchParams.delete('view');
        searchParams.delete('open');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [customers, searchParams, setSearchParams]);

  // Handle create parameter to auto-open create dialog from dashboard
  useEffect(() => {
    const shouldCreate = searchParams.get('create');
    if (shouldCreate === 'true' && !loading) {
      openDialog();
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [loading, searchParams, setSearchParams]);

  const loadCustomers = async () => {
    try {
      skipRef.current = 0;
      const res = await customersAPI.getAll({ skip: 0, limit: PAGE_SIZE });
      const total = parseInt(res.headers?.['x-total-count'] || '0', 10);
      setTotalCustomers(total);
      setCustomers(res.data);
      skipRef.current = res.data.length;
      setHasMore(res.data.length < total);
    } catch (error) {
      toast.error('Ошибка загрузки клиентов');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreCustomers = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await customersAPI.getAll({ skip: skipRef.current, limit: PAGE_SIZE });
      const total = parseInt(res.headers?.['x-total-count'] || '0', 10);
      setTotalCustomers(total);
      setCustomers(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const newCustomers = res.data.filter(c => !existingIds.has(c.id));
        return [...prev, ...newCustomers];
      });
      skipRef.current += res.data.length;
      setHasMore(skipRef.current < total);
    } catch (error) {
      console.error('Error loading more customers:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreCustomers();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadMoreCustomers]);

  const openDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name,
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        notes: customer.notes || '',
        social_links: customer.social_links || {},
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        name: '', email: '', phone: '', address: '', notes: '',
        social_links: {},
      });
    }
    setDialogOpen(true);
  };

  const vcfInputRef = React.useRef(null);

  const parseVCard = (text) => {
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    let name = '', phone = '', email = '', address = '';
    for (const line of lines) {
      const upper = line.toUpperCase();
      if (upper.startsWith('FN:') || upper.startsWith('FN;')) {
        name = line.substring(line.indexOf(':') + 1).trim();
      } else if (upper.startsWith('TEL:') || upper.startsWith('TEL;')) {
        phone = line.substring(line.indexOf(':') + 1).trim();
      } else if (upper.startsWith('EMAIL:') || upper.startsWith('EMAIL;')) {
        email = line.substring(line.indexOf(':') + 1).trim();
      } else if (upper.startsWith('ADR:') || upper.startsWith('ADR;')) {
        const raw = line.substring(line.indexOf(':') + 1);
        address = raw.split(';').map(s => s.trim()).filter(Boolean).join(', ');
      }
    }
    return { name, phone, email, address };
  };

  const handleVcfImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      if (!text.includes('BEGIN:VCARD')) {
        toast.error('Неверный формат файла. Выберите .vcf файл контакта');
        return;
      }
      const data = parseVCard(text);
      setEditingCustomer(null);
      setFormData({
        name: data.name,
        phone: data.phone,
        email: data.email,
        address: data.address,
        notes: '',
        social_links: {},
      });
      setDialogOpen(true);
      toast.success(`Контакт "${data.name}" загружен`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const importFromContacts = async () => {
    // Try native Contact Picker API first (Android Chrome)
    if ('contacts' in navigator && 'ContactsManager' in window) {
      try {
        const props = ['name', 'tel', 'email', 'address'];
        const opts = { multiple: false };
        const contacts = await navigator.contacts.select(props, opts);
        if (contacts && contacts.length > 0) {
          const contact = contacts[0];
          setEditingCustomer(null);
          setFormData({
            name: contact.name?.[0] || '',
            phone: contact.tel?.[0] || '',
            email: contact.email?.[0] || '',
            address: contact.address?.[0]
              ? [contact.address[0].streetAddress, contact.address[0].city, contact.address[0].region].filter(Boolean).join(', ')
              : '',
            notes: '',
            social_links: {},
          });
          setDialogOpen(true);
        }
        return;
      } catch (err) {
        if (err.name !== 'TypeError') {
          console.error('Contact Picker error:', err);
        }
      }
    }
    // Fallback: VCF file import (iOS, Desktop)
    vcfInputRef.current?.click();
  };

  const updateSocialLink = (type, value) => {
    setFormData({
      ...formData,
      social_links: { ...formData.social_links, [type]: value }
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Введите имя клиента');
      return;
    }

    setSaving(true);
    try {
      const socialLinks = Object.fromEntries(
        Object.entries(formData.social_links).filter(([_, v]) => v && v.trim())
      );

      const data = {
        ...formData,
        social_links: Object.keys(socialLinks).length > 0 ? socialLinks : null,
      };

      if (editingCustomer) {
        await customersAPI.update(editingCustomer.id, data);
        toast.success('Клиент обновлён');
      } else {
        await customersAPI.create(data);
        toast.success('Клиент добавлен');
      }
      setDialogOpen(false);
      loadCustomers();
    } catch (error) {
      if (error.response?.status === 403 && !editingCustomer) {
        setDialogOpen(false);
        setPaywallOpen(true);
      } else {
        toast.error('Ошибка сохранения');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const customer = customers.find(c => c.id === id);
    const customerName = customer?.name || 'этого клиента';
    if (!window.confirm(`Удалить клиента "${customerName}"?\n\nЭто действие нельзя отменить. Все данные клиента будут потеряны.`)) return;
    try {
      await customersAPI.delete(id);
      toast.success('Клиент удалён');
      loadCustomers();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const filteredCustomers = customers.filter(customer => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const socialValues = customer.social_links
      ? Object.values(customer.social_links).filter(Boolean).map(v => v.toLowerCase())
      : [];
    return (
      customer.name.toLowerCase().includes(query) ||
      customer.email?.toLowerCase().includes(query) ||
      customer.phone?.toLowerCase().includes(query) ||
      customer.address?.toLowerCase().includes(query) ||
      socialValues.some(v => v.includes(query))
    );
  });

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Клиенты</h1>
          <p className="text-sm text-muted-foreground">База клиентов</p>
        </div>
        <div className="flex gap-2">
          <input
            ref={vcfInputRef}
            type="file"
            accept=".vcf,.vcard"
            className="hidden"
            onChange={handleVcfImport}
          />
          <Button variant="outline" onClick={importFromContacts} data-testid="import-contact-btn">
            <BookUser className="h-4 w-4 mr-2" />
            Из контактов
          </Button>
          <Button onClick={() => openDialog()} data-testid="add-customer-btn">
            <Plus className="h-4 w-4 mr-2" />
            Добавить клиента
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по имени, телефону, email, адресу, соцсетям..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-customers-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customers Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 rounded-lg" />)}
        </div>
      ) : filteredCustomers.length === 0 ? (
        <Card className="mt-2">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="h-16 w-16 text-muted-foreground/30 mb-4" />
            <p className="text-lg font-medium">Нет клиентов</p>
            <p className="text-muted-foreground mb-1">Добавьте первого клиента</p>
            <p className="text-xs text-muted-foreground/60 mb-4">YaDela.ru — твои дела проще</p>
            <Button onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Добавить клиента
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
          {filteredCustomers.map((customer) => {
            const socialLinks = customer.social_links || {};
            const hasSocials = Object.values(socialLinks).some(v => v);
            return (
              <Card
                key={customer.id}
                className="cursor-pointer hover:shadow-md transition-all hover:border-primary/20"
                onClick={() => setViewingCustomer(customer)}
                data-testid={`customer-card-${customer.id}`}
              >
                <CardContent className="p-3">
                  {/* Header: Name + Actions */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm truncate">{customer.name}</h3>
                    </div>
                    <div className="flex items-center gap-0 shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewingCustomer(customer)}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog(customer)}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(customer.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Contact Info: Phone → Email → Address */}
                  <div className="flex flex-col mb-2.5" onClick={(e) => e.stopPropagation()}>
                    {customer.phone && (
                      <a href={`tel:${customer.phone}`} className="flex items-center gap-2 text-sm hover:text-foreground transition-colors group py-1">
                        <div className="h-6 w-6 rounded-md bg-green-500/10 flex items-center justify-center shrink-0">
                          <Phone className="h-3 w-3 text-green-600" />
                        </div>
                        <span className="text-muted-foreground group-hover:text-foreground truncate text-xs">{customer.phone}</span>
                      </a>
                    )}
                    {customer.email && (
                      <a href={`mailto:${customer.email}`} className="flex items-center gap-2 text-sm hover:text-foreground transition-colors group py-1">
                        <div className="h-6 w-6 rounded-md bg-blue-500/10 flex items-center justify-center shrink-0">
                          <Mail className="h-3 w-3 text-blue-600" />
                        </div>
                        <span className="text-muted-foreground group-hover:text-foreground truncate text-xs">{customer.email}</span>
                      </a>
                    )}
                    {customer.address && (
                      <div className="flex items-center gap-2 text-sm py-1">
                        <div className="h-6 w-6 rounded-md bg-rose-500/10 flex items-center justify-center shrink-0">
                          <MapPin className="h-3 w-3 text-rose-500" />
                        </div>
                        <span className="text-muted-foreground truncate text-xs">{customer.address}</span>
                      </div>
                    )}
                    {!customer.phone && !customer.email && !customer.address && (
                      <p className="text-xs text-muted-foreground py-1">Контакты не указаны</p>
                    )}
                  </div>

                  {/* Footer: Social + Stats */}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <div className="flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
                      {hasSocials ? (
                        Object.entries(socialLinks).map(([key, value]) => (
                          value && <SocialLinkButton key={key} type={key} value={value} size="sm" />
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">Нет соцсетей</span>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-base font-bold">{formatCurrency(customer.total_spent || 0)}</p>
                      <p className="text-xs text-muted-foreground">{customer.total_orders || 0} заказов</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Infinite scroll sentinel + counter */}
      <div ref={loadMoreRef} className="py-4 text-center">
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Загрузка...</span>
          </div>
        )}
        {!loading && !loadingMore && filteredCustomers.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Показано {filteredCustomers.length} из {totalCustomers}
            {!hasMore && totalCustomers > 0 && ' — все клиенты загружены'}
          </p>
        )}
      </div>

      {/* Customer View Card */}
      {viewingCustomer && (
        <CustomerCard 
          customer={viewingCustomer} 
          onClose={() => setViewingCustomer(null)}
          onEdit={() => {
            setViewingCustomer(null);
            openDialog(viewingCustomer);
          }}
        />
      )}

      {/* Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>{editingCustomer ? 'Редактировать клиента' : 'Новый клиент'}</DialogTitle>
            <DialogDescription>Заполните информацию о клиенте</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="space-y-2">
              <Label>Имя *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Имя клиента"
                data-testid="customer-name-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Телефон</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^\d+]/g, '');
                    // Extract digits only
                    const digits = val.replace(/\D/g, '');
                    // Detect Russian number: starts with +7, +8, 7, 8
                    const isRussian = val.startsWith('+7') || val.startsWith('+8') || 
                                      (!val.startsWith('+') && (digits.startsWith('7') || digits.startsWith('8')));
                    if (isRussian && digits.length > 0) {
                      // Normalize: strip leading 7 or 8, take up to 10 remaining digits
                      const core = (digits.startsWith('7') || digits.startsWith('8')) ? digits.slice(1) : digits;
                      let formatted = '+7';
                      if (core.length > 0) formatted += ' (' + core.slice(0, 3);
                      if (core.length >= 3) formatted += ') ' + core.slice(3, 6);
                      if (core.length >= 6) formatted += '-' + core.slice(6, 8);
                      if (core.length >= 8) formatted += '-' + core.slice(8, 10);
                      val = formatted;
                    }
                    setFormData({ ...formData, phone: val });
                  }}
                  placeholder="+7 (999) 123-45-67"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  className={formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? 'border-destructive' : ''}
                />
                {formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
                  <p className="text-xs text-destructive">Некорректный формат email</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Адрес</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Адрес доставки"
              />
            </div>

            {/* Social Links with hints */}
            <Separator />
            <div className="space-y-3">
              <Label className="text-base font-semibold">Соцсети для связи</Label>
              <p className="text-xs text-muted-foreground">Добавьте ссылки на соцсети клиента. Можно указать username, ID или полную ссылку.</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SocialLinkInput type="telegram" value={formData.social_links.telegram} onChange={(v) => updateSocialLink('telegram', v)} />
                <SocialLinkInput type="whatsapp" value={formData.social_links.whatsapp} onChange={(v) => updateSocialLink('whatsapp', v)} />
                <SocialLinkInput type="instagram" value={formData.social_links.instagram} onChange={(v) => updateSocialLink('instagram', v)} />
                <SocialLinkInput type="vk" value={formData.social_links.vk} onChange={(v) => updateSocialLink('vk', v)} />
                <SocialLinkInput type="ok" value={formData.social_links.ok} onChange={(v) => updateSocialLink('ok', v)} />
                <SocialLinkInput type="vkmax" value={formData.social_links.vkmax} onChange={(v) => updateSocialLink('vkmax', v)} />
              </div>
            </div>

            <Separator />
            <div className="space-y-2">
              <Label>Заметки</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Дополнительная информация"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="flex flex-row justify-between gap-2 pt-2">
            <Button variant="outline" size="lg" onClick={() => setDialogOpen(false)}>Отмена</Button>
            <Button size="lg" onClick={handleSave} disabled={saving} data-testid="save-customer-btn">
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaywallDialog
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        resourceType="customers"
        limit={3}
      />
    </div>
  );
}
